/**
 * Structure interaction logic: activate listening posts, data vaults, antennas, etc.
 * Pure functions — no side effects, no rendering.
 */

import type { PlayerState, PlanetState, RuinState, InteractionProgress } from '../types';
import { INTERACT_RANGE, INTERACT_MOVEMENT_COST } from '../constants';
import { arcDistance } from './gameState';

/**
 * Check if a player can interact with a ruin.
 */
export function canInteract(
  player: PlayerState,
  ruin: RuinState,
  planets: PlanetState[]
): boolean {
  if (!player.alive || player.airborne) return false;
  if (!ruin.interactable) return false;
  if (player.planetIndex !== ruin.planetIndex) return false;
  if (player.movementRemaining < INTERACT_MOVEMENT_COST) return false;

  const planet = planets[player.planetIndex];
  if (!planet) return false;

  const dist = arcDistance(player.surfaceDir, ruin.surfaceDir, planet.radius);
  return dist <= INTERACT_RANGE;
}

/**
 * Start an interaction — adds a new progress entry.
 */
export function startInteraction(
  progress: InteractionProgress[],
  playerId: number,
  teamIndex: number,
  ruinId: number,
  turnsRequired: number
): InteractionProgress[] {
  // Don't start if already interacting with this ruin
  if (progress.some(p => p.playerId === playerId && p.ruinId === ruinId)) {
    return progress;
  }
  return [
    ...progress,
    { ruinId, playerId, teamIndex, turnsRemaining: turnsRequired },
  ];
}

/**
 * Tick an interaction — decrement turnsRemaining.
 * Returns updated progress and whether the interaction just activated.
 */
export function tickInteraction(
  progress: InteractionProgress[],
  playerId: number,
  ruinId: number
): { progress: InteractionProgress[]; activated: boolean } {
  const idx = progress.findIndex(p => p.playerId === playerId && p.ruinId === ruinId);
  if (idx === -1) return { progress, activated: false };

  const entry = progress[idx];
  const newRemaining = entry.turnsRemaining - 1;

  if (newRemaining <= 0) {
    // Activated — remove from progress
    return {
      progress: progress.filter((_, i) => i !== idx),
      activated: true,
    };
  }

  // Still in progress
  return {
    progress: progress.map((p, i) =>
      i === idx ? { ...p, turnsRemaining: newRemaining } : p
    ),
    activated: false,
  };
}

/**
 * Cancel an interaction.
 */
export function cancelInteraction(
  progress: InteractionProgress[],
  playerId: number,
  ruinId: number
): InteractionProgress[] {
  return progress.filter(p => !(p.playerId === playerId && p.ruinId === ruinId));
}

/**
 * Get interaction progress for a specific ruin.
 */
export function getInteractionProgress(
  progress: InteractionProgress[],
  ruinId: number
): InteractionProgress | null {
  return progress.find(p => p.ruinId === ruinId) ?? null;
}

/**
 * Check for interruptions: cancel interactions where player is dead, off-planet, or out of range.
 */
export function checkInteractionInterruptions(
  progress: InteractionProgress[],
  players: PlayerState[],
  ruins: RuinState[],
  planets: PlanetState[]
): InteractionProgress[] {
  return progress.filter(entry => {
    const player = players.find(p => p.id === entry.playerId);
    if (!player || !player.alive || player.airborne) return false;

    const ruin = ruins.find(r => r.id === entry.ruinId);
    if (!ruin) return false;

    if (player.planetIndex !== ruin.planetIndex) return false;

    const planet = planets[player.planetIndex];
    if (!planet) return false;

    const dist = arcDistance(player.surfaceDir, ruin.surfaceDir, planet.radius);
    return dist <= INTERACT_RANGE;
  });
}
